<template>
    <div class="social-button">
        <SocialAuthError v-if="showError && $route.query.provider === provider" />
        <button
            :disabled="isSocialLoggingIn"
            class="button button--service social-button__button"
            type="button"
            @click.prevent="authenticate"
        >
            <template v-if="!isSocialLoggingIn">
                <template v-if="hasCustomText">
                    <slot />
                </template>
                <template v-else>
                    <svg-icon
                        v-if="provider === FACEBOOK"
                        :name="FACEBOOK"
                        class="social-button__fb-icon button__icon"
                    />
                    <svg-icon
                        v-if="provider === GOOGLE"
                        :name="GOOGLE"
                        class="social-button__google-icon button__icon"
                    />
                    {{ buttonText }}
                </template>
            </template>
            <LoadingIndicator v-if="isSocialLoggingIn" />
        </button>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import SocialAuthError from '~/components/onboarding/SocialAuthError';
import { FACEBOOK, GOOGLE } from '~/config/authMethods';

export default {
    components: {
        SocialAuthError,
        LoadingIndicator,
    },
    props: {
        provider: {
            type: String,
            required: true,
        },
        signupRedirectTo: {
            type: Object,
            default: null,
        },
        errorRedirectTo: {
            type: Object,
            default: null,
        },
        showError: {
            type: Boolean,
            default: true,
        },
        buttonText: {
            type: String,
            default() {
                return this.$t('modals.login.facebook_button');
            },
        },
    },
    data() {
        return {
            isSocialLoggingIn: false,
            hasCustomText: this.$slots.default,
            FACEBOOK,
            GOOGLE,
        };
    },
    computed: {
        ...mapGetters(['favoredAsset']),
        authState() {
            // Pass current $route data to oauth-callback page by using localStorage
            // so we can return to it when login succeeds
            const defaultRedirectTo = {
                name: this.getRouteBaseName(),
                query: this.$route.query,
                params: this.$route.params,
            };

            // Delete popup query because it conflicts with opening modals in userModalsHandlerMixin.js
            const query = { ...this.$route.query };
            delete query.popup;

            const redirectTo = defaultRedirectTo;
            redirectTo.query = query;

            return {
                loginRedirectTo: redirectTo,
                signupRedirectTo: this.signupRedirectTo || redirectTo,
                errorRedirect: this.errorRedirectTo || defaultRedirectTo,
            };
        },
    },
    beforeDestroy() {
        this.clearError();
    },
    methods: {
        clearError() {
            const query = { ...this.$route.query };

            if (query.error_code || query['auth-error'] || query.provider) {
                delete query.error_code;
                delete query['auth-error'];
                delete query.provider;
                this.$router.replace(this.localePath({ query }));
            }
        },
        handleError(error) {
            this.dispatchErrorEvent(error);
            this.$sentry.captureException(error);

            const query = { ...this.$route.query };
            query['auth-error'] = error.slug;
            query.provider = this.provider;
            this.$router.replace(this.localePath({ query }));
        },
        dispatchErrorEvent(error) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'login',
                    category: 'Errors',
                    action: `oauth: ${this.provider} failed`,
                    label: error.message,
                });
            }
        },
        async authenticate() {
            this.isSocialLoggingIn = true;

            this.$emit('auth-started', this.provider);

            if (this.$route.query['auth-error'] || this.$route.query.error_code) {
                this.clearError();
            }

            // Same as in oauth-callback.vue
            const loginUrl = `${document.location.origin}${this.localePath(
                { name: 'oauth-callback', query: { provider: this.provider } },
                this.$i18n.locale
            )}`;

            try {
                const url = await this.$authService.getOAuthRedirectUrl(this.provider, loginUrl);
                document.location = url;
                localStorage.setItem('auth.state', JSON.stringify(this.authState));

                if (this.favoredAsset !== null) {
                    localStorage.setItem('favored_asset', JSON.stringify(this.favoredAsset));
                }

                // Timeout used to avoid resetting the state before the redirect is complete
                setTimeout(() => {
                    this.isSocialLoggingIn = false;
                }, 1000);
            } catch (error) {
                this.handleError(error);
                this.isSocialLoggingIn = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.social-button {
    &__notice {
        margin-bottom: 20px;
    }

    &__button.button--service {
        width: 100%;
    }

    &__google-icon {
        margin-right: 10px;
    }

    &__fb-icon {
        margin-right: 5px;
    }
}
</style>
